import { BookingDto } from "../../dto";
import {
  dateToComparableNumber,
  equalDatesCalendar,
  getBookingCalendarCellId,
  getDateCalendar,
  parseBookingCellId,
} from "../../helpers";
import { BookingCellId } from "../../models";
import {
  passengersListBookingAtom,
  selectedCellsBookingCalendarAtom,
  selectedTableRowsIdsAtomFamily,
  tableRowsAtomFamily,
} from "../atoms";
import { boundedSelector, boundedSelectorFamily } from "../selector-factories";
import { timezoneSelector } from "./system-settings.selectors";

export const bookingsSelector = boundedSelector<BookingDto[]>({
  key: "bookingsSelector",
  get: ({ get }) => {
    const bookings = get(tableRowsAtomFamily("booking")) as BookingDto[];
    return bookings;
  },
});

export const selectedBookingsSelector = boundedSelector<BookingDto[]>({
  key: "selectedBookingsSelector",
  get: ({ get }) => {
    const bookings = get(bookingsSelector);
    const selectedBookingIds = get(selectedTableRowsIdsAtomFamily("booking"));
    const selectedBookingIdsSet = new Set(selectedBookingIds);
    return bookings?.filter(x => selectedBookingIdsSet.has(x.id)) || [];
  },
});

export const selectedBookingsCountSelector = boundedSelector<number>({
  key: "selectedBookingsCountSelector",
  get: ({ get }) => {
    const selectedBooking = get(selectedBookingsSelector);
    return selectedBooking?.length || 0;
  },
});

export const passengersCountBookingSelector = boundedSelector<number>({
  key: "passengersCountBookingSelector",
  get: ({ get }) => {
    const passengers = get(passengersListBookingAtom);
    return passengers?.length ?? 0;
  },
});

export const cellBookingsSF = boundedSelectorFamily<
  BookingDto[],
  BookingCellId
>({
  key: "cellBookingsSF",
  get:
    cellId =>
    ({ get }) => {
      const bookings = get(bookingsSelector);
      const cellData = parseBookingCellId(cellId);
      const timezone = get(timezoneSelector);
      return bookings
        .filter(
          x =>
            x.passengerId === cellData.passengerId &&
            equalDatesCalendar(x.bookTime, cellData.date, timezone),
        )
        .sort(
          (a, b) =>
            dateToComparableNumber(a.bookTime) -
            dateToComparableNumber(b.bookTime),
        );
    },
});

/*
 * Made for React Suspense fallback using
 * In case we want showing progress - logic should be moved inside component and use component state for
 * updating state
 * Another possible approach - making  calculation in webworker using such library as [useWorker](https://github.com/alewin/useWorker)
 * */
export const selectedCellsBookingsSelector = boundedSelector<BookingDto[]>({
  key: "selectedCellsBookingIdsSF",
  get: ({ get }) => {
    const bookings = get(bookingsSelector);
    const selectedCellsIds = get(selectedCellsBookingCalendarAtom);
    const timeZone = get(timezoneSelector);

    const result: BookingDto[] = [];
    const bookingsCellsMap = new Map<string, Set<BookingDto>>();

    for (const booking of bookings) {
      const bookingCellId = getBookingCalendarCellId(
        booking.passengerId,
        getDateCalendar(booking.bookTime, timeZone),
      );
      const prevBookingsSet = bookingsCellsMap.get(bookingCellId) ?? new Set();
      prevBookingsSet.add(booking);
      bookingsCellsMap.set(bookingCellId, prevBookingsSet);
    }

    for (const selectedCellsId of selectedCellsIds) {
      if (bookingsCellsMap.has(selectedCellsId)) {
        const bookingsFromCell = bookingsCellsMap.get(selectedCellsId);

        if (!bookingsFromCell) {
          continue;
        }

        result.push(...bookingsFromCell);
      }
    }

    return result;
  },
});
